import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatCLP } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Certificate, ChartLineUp, Buildings, ArrowRight,
  Star, Clock, MonitorPlay, CheckCircle,
} from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function Home() {
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data.slice(0, 3))).catch(() => {}); }, []);

  const features = [
    { icon: MonitorPlay, title: "100% Online", desc: "Estudia a tu ritmo desde cualquier dispositivo." },
    { icon: Certificate, title: "Certificado con folio", desc: "Certificación verificable con QR y folio único." },
    { icon: ShieldCheck, title: "Control de asistencia", desc: "Registro de actividad para cumplimiento SENCE." },
    { icon: Buildings, title: "Planes empresa", desc: "Capacita a tu equipo y mide su avance." },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="absolute -top-24 -right-24 w-[36rem] h-[36rem] bg-blue-200/30 blur-3xl rounded-full hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-overline text-primary mb-4 font-mono">Organismo Técnico de Capacitación</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-900 tracking-tight leading-none text-foreground">
              Capacitación profesional con <span className="text-primary">certificación real.</span>
            </h1>
            <p className="text-muted-foreground text-lg mt-6 leading-relaxed max-w-xl">
              Cursos online para trabajadores y empresas de Chile. Aprueba, certifícate y demuestra tus competencias con un folio verificable.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" onClick={() => navigate("/catalogo")} data-testid="hero-catalog-btn"
                className="bg-primary hover:bg-blue-600 text-white gap-2">
                Ver catálogo <ArrowRight size={18} />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/register")} data-testid="hero-company-btn"
                className="gap-2">
                Soy empresa
              </Button>
            </div>
            <div className="flex gap-8 mt-10">
              {[["+50", "Cursos"], ["12.000", "Alumnos"], ["4.8★", "Valoración"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-2xl font-800 text-foreground">{n}</div>
                  <div className="text-sm text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-border">
              <img src={HERO} alt="Capacitación profesional" className="w-full h-[300px] lg:h-[400px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-border p-4 items-center gap-3 hidden sm:flex">
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center"><Certificate size={22} className="text-primary" weight="duotone" /></div>
              <div>
                <div className="font-display font-800 text-sm">Certificado con folio</div>
                <div className="text-xs text-muted-foreground">Verificable con QR</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="bg-white border border-border rounded-lg p-6 transition-transform hover:-translate-y-[2px] hover:shadow-sm animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                  <Icon size={24} className="text-primary" weight="duotone" />
                </div>
                <h3 className="font-display font-700 text-lg tracking-tight">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured courses */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-overline text-primary mb-2 font-mono">Destacados</p>
            <h2 className="font-display text-3xl font-800 tracking-tight">Cursos más populares</h2>
          </div>
          <Link to="/catalogo" className="text-sm text-primary font-medium hover:underline hidden sm:flex items-center gap-1">
            Ver todos <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => (
            <Link key={c.id} to={`/curso/${c.id}`} data-testid={`home-course-${c.id}`}
              className="group bg-white border border-border rounded-lg overflow-hidden transition-transform hover:-translate-y-[3px] hover:shadow-md">
              <div className="aspect-video overflow-hidden bg-secondary">
                <img src={c.image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="p-5">
                <span className="text-xs font-mono text-primary uppercase tracking-wide">{c.category}</span>
                <h3 className="font-display font-700 text-lg tracking-tight mt-1 leading-snug">{c.title}</h3>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-3">
                  <span className="flex items-center gap-1"><Clock size={15} /> {c.duration_hours}h</span>
                  <span className="flex items-center gap-1"><Star size={15} weight="fill" className="text-accent" /> {c.rating}</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="font-display font-800 text-xl">{formatCLP(c.price)}</span>
                  <ArrowRight size={18} className="text-primary" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* B2B banner */}
      <section className="bg-primary">
        <div className="max-w-7xl mx-auto px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div>
            <h2 className="font-display text-3xl font-800 tracking-tight">¿Capacitas a un equipo?</h2>
            <p className="text-blue-100 mt-2 max-w-lg">Compra cupos masivos, asigna cursos y mide el avance de tus trabajadores desde un solo panel.</p>
          </div>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="b2b-cta"
            className="bg-white text-primary hover:bg-blue-50 whitespace-nowrap gap-2">
            Crear cuenta empresa <ArrowRight size={18} />
          </Button>
        </div>
      </section>
    </div>
  );
}
